import React, { memo, useMemo, useCallback, useState, useRef, useEffect, ChangeEvent } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { useCustomReports } from 'Context/CustomReports';
import classes from './jobTimeline.module.css';

function formatTimestamp(timestamp: string): string {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function capitalizeEvent(event: string): string {
  if (!event) return '';
  return event.charAt(0).toUpperCase() + event.slice(1);
}

function formatEntityType(type: string): string {
  if (!type) return '';
  return type.replace(/^Ghl/, 'CRM ');
}

function JobTimeline() {
  const { projectList, projectAudits, fetchProjectList, fetchProjectAudits }: any = useCustomReports();

  const [search, setSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<{ uid: string; alias: string } | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const hasSelected = selectedProject !== null;

  useEffect(() => {
    fetchProjectList();
  }, [fetchProjectList]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const projects = useMemo(() => {
    if (!projectList?.data || projectList.data.length === 0) return [];
    return projectList.data.map((row: any) => ({
      uid: row['project.uid'],
      alias: row['project.alias'] || row['project.uid'],
    }));
  }, [projectList?.data]);

  const events = useMemo(() => {
    if (!projectAudits?.data || projectAudits.data.length === 0) return [];
    return projectAudits.data.map((entry: any) => ({
      event: entry.event || 'unknown',
      entityType: entry.entityType || '',
      changes: entry.changes || [],
      user: entry.userName || (entry.userId ? `User #${entry.userId}` : 'System'),
      timestamp: entry.timestamp,
      source: entry.source,
    }));
  }, [projectAudits?.data]);

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setSearch(val);
      setDropdownOpen(true);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchProjectList(val);
      }, 300);
    },
    [fetchProjectList]
  );

  const handleSelectProject = useCallback(
    (project: { uid: string; alias: string }) => {
      setSelectedProject(project);
      setSearch(`${project.uid} — ${project.alias}`);
      setDropdownOpen(false);
      fetchProjectAudits(project.uid);
    },
    [fetchProjectAudits]
  );

  const handleFocus = useCallback(() => {
    setDropdownOpen(true);
    if (!search) fetchProjectList('');
  }, [fetchProjectList, search]);

  return (
    <div className={classes.pageContainer}>
      <h4 className={classes.pageTitle}>Job Timeline</h4>
      <p className={classes.pageSubtitle}>Chronological timeline of project events</p>
      <div className={classes.selectWrapper} ref={wrapperRef}>
        <input
          type="text"
          className={classes.searchInput}
          placeholder="Search projects by ID or name..."
          value={search}
          onChange={handleSearchChange}
          onFocus={handleFocus}
        />
        {dropdownOpen && (
          <div className={classes.dropdown}>
            {projectList?.loading && (
              <div className={classes.dropdownLoading}>Searching...</div>
            )}
            {!projectList?.loading && projectList?.error && (
              <div className={classes.dropdownError}>{projectList.error}</div>
            )}
            {!projectList?.loading && !projectList?.error && projects.length === 0 && (
              <div className={classes.dropdownEmpty}>No projects found</div>
            )}
            {!projectList?.loading &&
              !projectList?.error &&
              projects.map((p: any) => (
                <button
                  key={p.uid}
                  type="button"
                  className={classes.dropdownItem}
                  onClick={() => handleSelectProject(p)}
                >
                  {p.uid} — {p.alias}
                </button>
              ))}
          </div>
        )}
      </div>

      {projectAudits?.loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {projectAudits?.error && !projectAudits?.loading && (
        <div className="alert alert-danger" role="alert">
          {projectAudits.error}
        </div>
      )}

      {!projectAudits?.loading && !projectAudits?.error && events.length > 0 && (
        <div className={classes.timeline}>
          {events.map((evt: any, idx: number) => (
            <div className={classes.event} key={idx}>
              <div className={`${classes.eventDot} ${classes[evt.event] || ''}`} />
              <div className={classes.eventContent}>
                <div className={classes.eventHeader}>
                  <span className={classes.eventType}>
                    {capitalizeEvent(evt.event)} {formatEntityType(evt.entityType)}
                  </span>
                  <span className={classes.eventTime}>{formatTimestamp(evt.timestamp)}</span>
                </div>
                <div className={classes.eventUser}>by {evt.user}</div>
                {evt.changes.length > 0 && (
                  <div className={classes.eventChanges}>
                    {evt.changes.map((change: any, cIdx: number) => (
                      <div key={cIdx}>{change.display}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {hasSelected && !projectAudits?.loading && !projectAudits?.error && events.length === 0 && (
        <p className={classes.emptyState}>No events found for this project.</p>
      )}

      {!hasSelected && !projectAudits?.loading && (
        <p className={classes.emptyState}>Select a project to view its timeline</p>
      )}
    </div>
  );
}

const JobTimelineMemo = memo(JobTimeline, areEqual);
export { JobTimelineMemo as JobTimeline };
